/**
 * ivyx-classic ApplicationProfile (8.E).
 *
 * The backward-compatibility default profile. Reproduces 100% of the
 * pre-ApplicationProfile behavior: all 8 shell modules active, capability
 * gateway fully open, classic-editor layout, auto theme.
 *
 * Anayasal invariant: `ivyx-classic` boot must pass all existing
 * ivyx E2E tests without any behavioral change. When no profile is
 * passed to `bootstrap()`, this profile is loaded automatically.
 */

import type { shell } from 'punica';

import { IVYX_MARK } from './brandMark.js';

type ApplicationProfile = shell.Profile.ApplicationProfile;

export const IVYX_CLASSIC_PROFILE_ID = 'ivyx-classic';

/**
 * Classic editor root layout — mirrors the pre-ApplicationProfile `classicEditorProfile`
 * from `src/impl/shell/layout/index.ts`.
 */
const CLASSIC_EDITOR_LAYOUT_ROOT: shell.Layout.LayoutNode = {
  type: 'row',
  sizes: [70, 30],
  children: [
    {
      type: 'column',
      sizes: [80, 20],
      children: [
        { type: 'slot', id: 'content', role: 'content' },
        { type: 'slot', id: 'panel', role: 'panel' }
      ]
    },
    { type: 'slot', id: 'secondarySidebar', role: 'secondarySidebar' }
  ]
};

/**
 * Top strip: the brand mark on the left, the workspace in the middle, and
 * nothing else of its own.
 *
 * The settings and account buttons used to live here. They moved to where each
 * host already keeps that kind of action: the Electron host's native menu
 * (Settings under the app menu on macOS and under File elsewhere; Check for
 * Updates and Activate License under Help on all three), and a browser host,
 * which has no menu bar, puts what it needs back on the bar itself through
 * `AppBar.manager.addItem` — the same seam the Electron host uses for its menu
 * button. A profile cannot tell those hosts apart, which is why the spec now
 * declares neither.
 */
const APP_BAR: shell.AppBar.AppBarSpec = {
  height: 30,
  title: 'workspace',
  items: [
    // The layout popover: one tile per region (sidebar, panel, right
    // sidebar), each a `shell.layout.toggle*` command. Declared first so it
    // sits left of whatever a host adds (`Agent ↗`, the account chip).
    {
      id: 'appbar-layout',
      icon: '<i class="fa-solid fa-table-columns"></i>',
      title: 'Layout',
      alignment: 'right',
      command: 'shell.layout.openMenu'
    }
  ]
};

export const ivyxClassicProfile: ApplicationProfile = {
  schemaVersion: '1',

  identity: {
    id: IVYX_CLASSIC_PROFILE_ID,
    name: 'ivyx',
    version: '1.0.0',
    audience: 'ml-engineer',
    branding: {
      productName: 'IVYX Studio',
      logo: IVYX_MARK
    }
  },

  shell: {
    activeModules: [
      'shell.activityBar',
      'shell.statusbar',
      'shell.view',
      'shell.keyboardShortcuts',
      'shell.layout',
      // ViewRegistry and the app bar depend on shell.layout — keep them after
      // layout (module registration validates dependency order).
      'shell.views',
      'shell.appBar',
      'shell.contentTabs',
      'shell.panelTabs',
      'shell.panelLayout',
      'flow.shell'
    ],
    layout: {
      id: 'classic-editor',
      root: CLASSIC_EDITOR_LAYOUT_ROOT
    },
    appBar: APP_BAR
  },

  theme: {
    mode: 'auto',
    density: 'comfortable',
    branding: {
      productName: 'IVYX Studio'
    }
  },

  capabilities: {
    // Fully open — no gateway restrictions for ivyx profile.
    allowedIds: '*'
  },

  /**
   * Interface languages this product ships.
   *
   * `punica.extensions.i18n.manager` reads this: it bounds what the Settings
   * language picker offers and what a stored or OS-detected language is allowed
   * to resolve to. Shipping a new language means adding it here and adding the
   * dictionary to each extension — not editing a list inside a settings panel.
   */
  localization: {
    default: 'en',
    supported: ['en', 'tr']
  }
};
